import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Copy, MoreHorizontal, Search } from 'lucide-react';
import { useMutation } from 'urql';
import Swal from 'sweetalert2';
import GuidesFiltersFields from '@/modules/guias/GuidesFiltersFields.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { hasPermission } from '@/utils/permissions.js';
import {
  GUIDE_FILTERS_INITIAL_STATE,
  buildGuideFilters,
  summarizeGuideFilters,
} from '@/modules/guias/guidesFilters.js';
import { fetchGuideDetail, fetchGuideOperationalContext, fetchGuides } from '@/services/guides.js';
import { CAMBIAR_ESTADO_GUIA } from '@/graphql/trazabilidadAlertas.gql.js';
import { printGuiaDirectly, printGuiaPdf } from '@/modules/guias/services/printGuia.js';
import { useOperatorCompactQaScreen } from '@/utils/operatorCompactQa.js';
import { formatNumeroGuia } from '@/lib/guias/validation.js';
import ClienteQuickViewModal from '@/components/guias/ClienteQuickViewModal.jsx';
import { sortGuidesByNumber } from './guidePrintQueue.js';

const LOGISTIC_STATUS_STYLES = {
  EMITIDA:
    'border-[rgba(var(--brand),0.18)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand-700))] dark:text-[rgb(var(--brand))]',
  EN_FOJA:
    'border-slate-200/80 bg-slate-50/80 text-slate-700 dark:border-slate-700/80 dark:bg-slate-900/60 dark:text-slate-200',
  FOJA_CERRADA:
    'border-amber-200/80 bg-amber-50/80 text-amber-700 dark:border-amber-700/70 dark:bg-amber-950/40 dark:text-amber-200',
  EN_TRANSITO_A_DESTINO:
    'border-cyan-200/80 bg-cyan-50/80 text-cyan-700 dark:border-cyan-700/70 dark:bg-cyan-950/40 dark:text-cyan-200',
  EN_TRANSITO_A_VIA:
    'border-violet-200/80 bg-violet-50/80 text-violet-700 dark:border-violet-700/70 dark:bg-violet-950/40 dark:text-violet-200',
  RECIBIDA_EN_VIA:
    'border-fuchsia-200/80 bg-fuchsia-50/80 text-fuchsia-700 dark:border-fuchsia-700/70 dark:bg-fuchsia-950/40 dark:text-fuchsia-200',
  PENDIENTE_RECANALIZACION:
    'border-rose-200/80 bg-rose-50/80 text-rose-700 dark:border-rose-700/70 dark:bg-rose-950/40 dark:text-rose-200',
  RECIBIDA_DESTINO:
    'border-cyan-200/80 bg-cyan-50/80 text-cyan-700 dark:border-cyan-700/70 dark:bg-cyan-950/40 dark:text-cyan-200',
  EN_REPARTO:
    'border-sky-200/80 bg-sky-50/80 text-sky-700 dark:border-sky-700/70 dark:bg-sky-950/40 dark:text-sky-200',
  ENTREGADA:
    'border-[rgba(var(--brand),0.18)] bg-[rgba(var(--brand),0.08)] text-[rgb(var(--brand-700))] dark:text-[rgb(var(--brand))]',
  RETENIDA:
    'border-red-200/80 bg-red-50/80 text-red-700 dark:border-red-700/70 dark:bg-red-950/40 dark:text-red-200',
  REPROGRAMADA:
    'border-orange-200/80 bg-orange-50/80 text-orange-700 dark:border-orange-700/70 dark:bg-orange-950/40 dark:text-orange-200',
  ANULADA:
    'border-[rgba(var(--border),0.8)] bg-[rgba(var(--bg),0.72)] text-[rgba(var(--fg),0.72)]',
  CANCELADA:
    'border-[rgba(var(--border),0.8)] bg-[rgba(var(--bg),0.72)] text-[rgba(var(--fg),0.72)]',
  BORRADOR:
    'border-[rgba(var(--border),0.72)] bg-[rgba(var(--subpanel),0.9)] text-[rgba(var(--fg),0.8)]',
  PENDIENTE:
    'border-[rgba(var(--border),0.72)] bg-[rgba(var(--subpanel),0.82)] text-[rgb(var(--fg))]',
};

const COMMERCIAL_STATUS_STYLES = {
  SIN_VALORIZAR: 'border-red-200/80 bg-red-50/80 text-red-700 dark:border-red-700/70 dark:bg-red-950/40 dark:text-red-200',
  PENDIENTE_VALORIZACION:
    'border-amber-200/80 bg-amber-50/80 text-amber-700 dark:border-amber-700/70 dark:bg-amber-950/40 dark:text-amber-200',
  VALORIZADA:
    'border-emerald-200/80 bg-emerald-50/80 text-emerald-700 dark:border-emerald-700/70 dark:bg-emerald-950/40 dark:text-emerald-200',
  FACTURADA:
    'border-indigo-200/80 bg-indigo-50/80 text-indigo-700 dark:border-indigo-700/70 dark:bg-indigo-950/40 dark:text-indigo-200',
  COBRADA:
    'border-teal-200/80 bg-teal-50/80 text-teal-700 dark:border-teal-700/70 dark:bg-teal-950/40 dark:text-teal-200',
  PENDIENTE_COBRO:
    'border-yellow-200/80 bg-yellow-50/80 text-yellow-700 dark:border-yellow-700/70 dark:bg-yellow-950/40 dark:text-yellow-200',
};

const ADVANCED_FILTER_FIELDS = [
  'estadoLogistico',
  'estadoComercial',
  'paymentCondition',
  'line',
  'branchId',
  'invoiceStatus',
  'collectionStatus',
  'party',
  'foja',
  'reparto',
  'remitoNumber',
  'sender',
  'receiver',
  'address',
];

const COMPACT_TABLE_BREAKPOINT = 1366;
const SHORT_VIEWPORT_HEIGHT = 768;
const NON_CANCELLABLE_STATES = new Set(['ANULADA', 'CANCELADA', 'ENTREGADA']);
const SORTABLE_COLUMNS = { guia: 'Guía', fecha: 'Fecha', destino: 'Destino', estado: 'Estado', importe: 'Importe' };

const FILTER_INPUT_CLASSNAME =
  'h-8 w-full rounded-lg border border-[rgba(var(--border),0.55)] bg-[rgb(var(--panel))] px-2.5 text-sm text-[rgb(var(--fg))] shadow-sm outline-none transition placeholder:text-[rgba(var(--muted),0.78)] focus:ring-2 focus:ring-[rgba(var(--ring),0.65)]';

const ACTION_BUTTON_CLASSNAME =
  'inline-flex h-8 items-center justify-center rounded-lg border px-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-[rgba(var(--ring),0.65)]';

const getViewportSize = () => ({
  width: typeof window === 'undefined' ? COMPACT_TABLE_BREAKPOINT + 1 : window.innerWidth,
  height: typeof window === 'undefined' ? SHORT_VIEWPORT_HEIGHT + 1 : window.innerHeight,
});

const getErrorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

const normalizeText = value => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const normalizeStatus = value => normalizeText(value).toUpperCase();

const formatCurrency = value => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  }).format(Number(value));
};

const formatNumber = (value, digits = 0) =>
  new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value || 0));

const toComparableId = value => {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
};

const ENTITY_NAME_FIELDS = ['razon_social', 'nombre', 'nombre_fantasia', 'descripcion', 'label', 'codigo'];

const isIdLikeText = value => /^(?:id|#)\s*\S+$/i.test(normalizeText(value)) || /^\d+$/.test(normalizeText(value));

const firstText = (...values) => {
  for (const value of values) {
    const text = normalizeText(value);
    if (text && !isIdLikeText(text)) return text;
  }
  return '';
};

const resolveEntityField = (row, keys, field) => {
  const searchKeys = Array.isArray(keys) ? keys : [keys];

  for (const key of searchKeys) {
    if (!key) continue;

    const directKey = `${key}_${field}`;
    const camelKey = `${key}${field.charAt(0).toUpperCase()}${field.slice(1)}`;
    const directValue = firstText(row?.[directKey], row?.[camelKey]);
    if (directValue) return directValue;

    const nested = row?.[key];
    if (nested && typeof nested === 'object') {
      const nestedValue = firstText(nested?.[field]);
      if (nestedValue) return nestedValue;
    }

    if (field === 'nombre' && typeof nested === 'string') {
      const nestedValue = firstText(nested);
      if (nestedValue) return nestedValue;
    }
  }

  return '';
};

const resolveEntityName = (row, keys) => {
  const searchKeys = Array.isArray(keys) ? keys : [keys];

  for (const key of searchKeys) {
    const direct = firstText(
      row?.[`${key}_nombre`],
      row?.[`${key}_razon_social`],
      row?.[`${key}_nombre_fantasia`],
      row?.[`${key}_descripcion`],
      row?.[`${key}_label`],
      row?.[`${key}_codigo`],
    );
    if (direct) return direct;

    for (const field of ENTITY_NAME_FIELDS) {
      const resolved = resolveEntityField(row, key, field);
      if (resolved) return resolved;
    }

    const nested = row?.[key];
    if (typeof nested === 'string') {
      const text = firstText(nested);
      if (text) return text;
    }
  }

  return '';
};

const resolveEntityId = (row, keys) => {
  const searchKeys = Array.isArray(keys) ? keys : [keys];

  for (const key of searchKeys) {
    const direct = firstText(
      row?.[`${key}_id`],
      row?.[`${key}Id`],
    );
    if (direct) return direct;

    const nested = row?.[key];
    if (nested && typeof nested === 'object') {
      const nestedId = firstText(nested?.id);
      if (nestedId) return nestedId;
    }

    if (typeof nested === 'number' || (typeof nested === 'string' && /^\d+$/.test(nested.trim()))) {
      const text = firstText(nested);
      if (text) return text;
    }
  }

  return '';
};

function resolveParticipantDisplay(row, key) {
  const name = resolveEntityName(row, key);
  const id = resolveEntityId(row, key);
  const displayName = name || 'Sin dato registrado';

  return {
    name: displayName,
    id,
    title: name || displayName,
  };
}

function formatLineaOperativa({ sucursalCodigo = '', lineaNumero = '' } = {}) {
  const codigo = normalizeText(sucursalCodigo);
  const lineaRaw = normalizeText(lineaNumero);
  if (!codigo && !lineaRaw) return '';

  const digits = lineaRaw.replace(/\D/g, '');
  const lineaFormateada = digits ? digits.padStart(8, '0') : lineaRaw;
  return codigo ? `${codigo}-${lineaFormateada}` : lineaFormateada;
}

const getActiveSucursalId = ({ sucursal, sucursalActiva, user }) =>
  toComparableId(
    sucursalActiva?.id ||
      sucursalActiva?.sucursal_id ||
      sucursal?.id ||
      sucursal?.sucursal_id ||
      user?.sucursal_activa_id ||
      user?.sucursal_id ||
      user?.sucursal?.id
  );

function inferPaymentCondition(row) {
  const explicit = normalizeStatus(row.condicion_pago);
  if (explicit) return explicit;
  const modalidad = normalizeStatus(row.modalidad_cobro || row.formaPago);
  if (modalidad.includes('ORIGEN')) return 'ORIGEN';
  if (modalidad.includes('DESTINO')) return 'DESTINO';
  if (modalidad.includes('CONTADO')) return 'CONTADO';
  if (modalidad.includes('CTA') || modalidad.includes('CUENTA')) return 'CUENTA_CORRIENTE';
  return '';
}

function formatPaymentConditionLabel(value) {
  const normalized = normalizeStatus(value);
  if (!normalized) return 'Condición sin dato';
  if (normalized === 'ORIGEN') return 'Pago en origen';
  if (normalized === 'DESTINO') return 'Pago en destino';
  if (normalized === 'CONTADO') return 'Contado';
  if (normalized === 'CUENTA_CORRIENTE') return 'Cuenta corriente';
  if (normalized === 'SIN_CARGO') return 'Sin cargo';
  return normalized.replaceAll('_', ' ');
}

function formatLogisticStatus(value) {
  const labels = { EMITIDA: 'Emitida', EN_FOJA: 'En foja', FOJA_CERRADA: 'En foja', EN_TRANSITO_A_DESTINO: 'En tránsito', EN_TRANSITO_A_VIA: 'En tránsito', RECIBIDA_EN_VIA: 'Recibida', RECIBIDA_DESTINO: 'Recibida', EN_REPARTO: 'En reparto', ENTREGADA: 'Entregada', RETENIDA: 'Retenida', REPROGRAMADA: 'Reprogramada', ANULADA: 'Anulada', CANCELADA: 'Anulada', PENDIENTE_RECANALIZACION: 'Reprogramada' };
  const normalized = normalizeStatus(value);
  return labels[normalized] || (normalized ? normalized.replaceAll('_', ' ') : 'Sin estado');
}

function deriveLogisticStatus(row) {
  return normalizeStatus(row.estado_logistico || row.estado) || 'SIN_DATO';
}

function deriveCommercialStatus(row) {
  const explicit = normalizeStatus(row.estado_comercial);
  if (explicit) return explicit;
  if (row.facturada) return 'FACTURADA';
  if (row.valorizada || Number(row.total_valorizado || 0) > 0) return 'VALORIZADA';
  return 'SIN_VALORIZAR';
}

function getCommercialAlert(row) {
  const status = deriveCommercialStatus(row);
  if (status === 'SIN_VALORIZAR' || status === 'PENDIENTE_VALORIZACION') return 'Pendiente de valorizar';
  if (status === 'PENDIENTE_COBRO') return 'Pendiente de cobro';
  if (normalizeStatus(row.estado_facturacion).includes('PENDIENTE')) return 'Pendiente de facturar';
  if (normalizeStatus(row.estado_cobranza).includes('DIFERENCIA')) return 'Con diferencia';
  return '';
}

function resolveGuideNumber(row) {
  return normalizeText(row.nro_guia || row.numero_guia || row.numero) || String(row.id);
}

function resolveFormattedGuideNumber(row) {
  const guideNumber = resolveGuideNumber(row);
  const puntoVenta = normalizeText(row.punto_venta_numero ?? row.puntoVentaNumero ?? row.punto_venta_id ?? row.puntoVentaId);
  if (!puntoVenta) return guideNumber;
  return formatNumeroGuia(puntoVenta, guideNumber);
}

function formatEmissionDate(value) {
  const dateText = normalizeText(value);
  if (!dateText) return 'Sin fecha';

  const isoDate = dateText.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;

  const date = new Date(dateText);
  return Number.isNaN(date.getTime()) ? dateText : new Intl.DateTimeFormat('es-AR').format(date);
}

function formatEmissionTime(value) {
  const match = normalizeText(value).match(/(?:T|\s)(\d{2}:\d{2})(?::\d{2})?/);
  return match ? match[1] : '';
}

function resolveRemitoNumber(row) {
  return normalizeText(row.remito || row.remito_numero || row.numero_remito);
}

function resolveBultos(row) {
  return Number(row.bultos ?? row.tot_bultos ?? 0);
}

function resolveKilos(row) {
  return Number(row.kg_reales ?? row.kilos ?? row.tot_kilos ?? 0);
}

function resolveDestination(row) {
  return normalizeText(row.localidad_destino || row.localidad_destino_nombre || row.destino) || '-';
}

function resolveOperationalBranch(row) {
  return (
    normalizeText(row.sucursal_via) ||
    normalizeText(row.sucursal_via_nombre) ||
    normalizeText(row.sucursal_destino) ||
    normalizeText(row.sucursal_destino_nombre) ||
    'Sucursal sin resolver'
  );
}

function canAnularGuide(row, activeSucursalId, user) {
  if (!hasPermission(user, 'GUIAS_ANULAR')) return false;
  const responsibleSucursalId = toComparableId(row.sucursal_actual_id || row.sucursal_origen_id);
  if (!activeSucursalId || !responsibleSucursalId || activeSucursalId !== responsibleSucursalId) return false;
  return !NON_CANCELLABLE_STATES.has(deriveLogisticStatus(row));
}

function StatusBadge({ status, tone = 'logistic', title, label }) {
  const normalized = normalizeStatus(status);
  const styleMap = tone === 'commercial' ? COMMERCIAL_STATUS_STYLES : LOGISTIC_STATUS_STYLES;

  return (
    <span
      title={title || normalized || '-'}
      className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold leading-none tracking-[0.06em] ${
        styleMap[normalized]
          || 'border-[rgba(var(--border),0.72)] bg-[rgba(var(--subpanel),0.9)] text-[rgba(var(--fg),0.82)]'
      }`}
    >
      {label || normalized || '-'}
    </span>
  );
}

function GuideCell({ row }) {
  const remito = resolveRemitoNumber(row);
  const lineaOperativa = row.linea_operativa
    || formatLineaOperativa({
      sucursalCodigo: row.sucursal_origen_codigo || row.sucursal_actual_codigo || row.sucursal_destino_codigo || row.sucursal_via_codigo,
      lineaNumero: row.linea_numero || row.linea,
    });
  return (
    <div className="min-w-0 space-y-0.5 leading-tight">
      <div className="line-clamp-1 text-[12px] font-semibold text-[rgb(var(--fg))]" title={resolveFormattedGuideNumber(row)}>
        {resolveFormattedGuideNumber(row)}
      </div>
      <div className="line-clamp-1 text-[10px] text-[rgba(var(--muted),0.84)]" title={formatEmissionDate(row.fecha_emision ?? row.fecha)}>
        Emisión: {formatEmissionDate(row.fecha_emision ?? row.fecha)}
      </div>
      <div className="line-clamp-1 text-[10.5px] text-[rgba(var(--fg),0.72)]" title={remito || 'Sin remito'}>
        Rem: {remito || '-'}
      </div>
      <div className="line-clamp-1 text-[10px] text-[rgba(var(--muted),0.84)]" title={lineaOperativa || 'Sin dato registrado'}>
        Línea: {lineaOperativa || 'Sin dato registrado'}
      </div>
      <div className="line-clamp-1 text-[10px] text-[rgba(var(--muted),0.84)]">
        {formatNumber(resolveBultos(row), 0)} bultos · {formatNumber(resolveKilos(row), 2)} kg
      </div>
    </div>
  );
}

function EntityLine({ prefix, label, value, id }) {
  const title = id ? `${value} (${label} #${id})` : value;

  return (
    <div className="flex min-w-0 items-center gap-1">
      <span className="shrink-0 text-[rgba(var(--muted),0.92)]">{prefix}</span>
      <span className="min-w-0 flex-1 line-clamp-1 text-[11px] font-medium text-[rgb(var(--fg))]" title={title}>
        {value}
      </span>
      {id ? <span className="shrink-0 text-[9px] font-semibold text-[rgba(var(--muted),0.72)]">#{id}</span> : null}
    </div>
  );
}

function CounterpartiesCell({ row }) {
  const remitente = resolveParticipantDisplay(row, 'remitente', 'Remitente');
  const destinatario = resolveParticipantDisplay(row, 'destinatario', 'Destinatario');

  return (
    <div className="min-w-0 space-y-0.5 leading-tight">
      <EntityLine prefix="Rem:" label="Remitente" value={remitente.name} id={remitente.id} />
      <EntityLine prefix="Des:" label="Destinatario" value={destinatario.name} id={destinatario.id} />
    </div>
  );
}

function DestinationCell({ row }) {
  const destination = resolveDestination(row);
  const branch = resolveOperationalBranch(row);
  const branchCode = row.sucursal_destino_codigo || row.sucursal_via_codigo || row.sucursal_actual_codigo || row.sucursal_origen_codigo;

  return (
    <div className="min-w-0 space-y-0.5 leading-tight">
      <div className="line-clamp-1 text-[11.5px] font-medium text-[rgb(var(--fg))]" title={destination}>
        {destination}
      </div>
      <div className="line-clamp-1 text-[10px] text-[rgba(var(--muted),0.84)]" title={branchCode ? `${branchCode} · ${branch}` : branch}>
        {branchCode ? `${branchCode} · ` : ''}
        {branch}
      </div>
    </div>
  );
}

function StatusesCell({ row }) {
  const logisticStatus = deriveLogisticStatus(row);
  const commercialStatus = deriveCommercialStatus(row);

  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <StatusBadge status={logisticStatus} title="Estado logístico" />
      <StatusBadge status={commercialStatus} tone="commercial" title="Estado comercial" />
    </div>
  );
}

function AmountCell({ row }) {
  return (
    <div className="min-w-0 text-right leading-tight">
      <div className="line-clamp-1 text-[11.5px] font-semibold text-[rgb(var(--fg))]">
        {formatCurrency(row.total_valorizado ?? row.total)}
      </div>
      <div className="line-clamp-1 text-[10px] text-[rgba(var(--muted),0.84)]">
        {formatPaymentConditionLabel(inferPaymentCondition(row))}
      </div>
    </div>
  );
}

function ValorizacionCell({ row }) {
  const total = Number(row.total_valorizado ?? row.total ?? 0);
  const status = deriveCommercialStatus(row);
  const hasValuation = total > 0 && status !== 'SIN_VALORIZAR' && status !== 'PENDIENTE_VALORIZACION';

  return (
    <div className="min-w-0 space-y-0.5 leading-tight">
      <div className="line-clamp-1 text-[11.5px] font-semibold text-[rgb(var(--fg))]" title={hasValuation ? 'Valorizada' : 'Pendiente'}>
        {hasValuation ? 'Valorizada' : 'Pendiente'}
      </div>
      <div className="line-clamp-1 text-[10px] text-[rgba(var(--muted),0.84)]" title={status.replaceAll('_', ' ')}>
        {status.replaceAll('_', ' ')}
      </div>
    </div>
  );
}

function OperationalGuideCell({ row, onCopy }) {
  const remito = resolveRemitoNumber(row);
  const remitosCount = Number(row.remitos_count || row.cantidad_remitos || 0);
  return <div className="min-w-max leading-tight"><div className="flex w-max items-center gap-1 whitespace-nowrap"><div className="shrink-0 whitespace-nowrap text-[12px] font-semibold" title={resolveFormattedGuideNumber(row)}>{resolveFormattedGuideNumber(row)}</div><button type="button" aria-label="Copiar número de guía" title="Copiar número de guía" onClick={event => { event.stopPropagation(); onCopy(resolveFormattedGuideNumber(row)); }} className="shrink-0 rounded p-0.5 text-[rgba(var(--muted),.8)] hover:bg-[rgba(var(--muted),.12)] focus:outline-none focus:ring-2 focus:ring-[rgba(var(--ring),.65)]"><Copy size={12} aria-hidden="true" /></button></div>{remito ? <div className="whitespace-nowrap text-[10.5px] text-[rgba(var(--fg),0.72)]">Rem. {remito}</div> : remitosCount > 1 ? <div className="whitespace-nowrap text-[10.5px] text-[rgba(var(--fg),0.72)]">{remitosCount} remitos</div> : null}</div>;
}

function OperationalDateCell({ row }) {
  const value = row.fecha_emision ?? row.fecha;
  const time = formatEmissionTime(value);
  return <div className="leading-tight"><div className="text-[11.5px] font-medium">{formatEmissionDate(value)}</div>{time ? <div className="text-[10px] text-[rgba(var(--muted),0.84)]">{time}</div> : null}</div>;
}

function OperationalParticipantCell({ row, participantKey, label, onOpenClient }) {
  const participant = resolveParticipantDisplay(row, participantKey, label);
  const paymentCondition = inferPaymentCondition(row);
  const payerHint =
    (participantKey === 'remitente' && paymentCondition === 'ORIGEN' && 'Pagador · origen') ||
    (participantKey === 'destinatario' && paymentCondition === 'DESTINO' && 'Pagador · destino');

  return (
    <div className="min-w-0 leading-tight">
      {participant.id ? <button type="button" onClick={event => { event.stopPropagation(); onOpenClient?.({ id: participant.id, name: participant.name, role: label }); }} className="block max-w-full truncate text-left text-[11.5px] font-medium underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-[rgba(var(--ring),.65)]" title={`Abrir ficha de ${participant.name}`}>{participant.name}</button> : <div className="truncate text-[11.5px] font-medium" title={participant.title}>{participant.name}</div>}
      {payerHint ? <div className="text-[10px] text-[rgba(var(--fg),0.72)]">{payerHint}</div> : null}
    </div>
  );
}

function OperationalStatusCell({ row }) {
  const status = deriveLogisticStatus(row);
  const alert = getCommercialAlert(row);
  return <div className="flex flex-col items-start gap-1"><StatusBadge status={status} label={formatLogisticStatus(status)} title={formatLogisticStatus(status)} />{alert ? <span className="text-[10px] text-amber-700 dark:text-amber-300">{alert}</span> : null}</div>;
}

function OperationalAmountCell({ row }) {
  const amount = row.total_valorizado ?? row.total;
  const isFree = normalizeStatus(inferPaymentCondition(row)) === 'SIN_CARGO';
  const hasAmount = amount !== null && amount !== undefined && (Number(amount) !== 0 || isFree);
  return <div className="text-right leading-tight"><div className="text-[11.5px] font-semibold">{hasAmount ? formatCurrency(amount) : 'Pendiente'}</div>{!hasAmount ? <div className="text-[10px] text-[rgba(var(--muted),0.84)]">Sin importe calculado</div> : null}</div>;
}

function ExpandedGuideRow({ row, detail, loading }) {
  if (loading) return <div className="grid animate-pulse grid-cols-3 gap-2 px-3 py-3"><div className="h-12 rounded bg-[rgba(var(--muted),.1)]" /><div className="h-12 rounded bg-[rgba(var(--muted),.1)]" /><div className="h-12 rounded bg-[rgba(var(--muted),.1)]" /></div>;
  row = detail?.data || detail || row;
  const linea = row.linea_operativa || formatLineaOperativa({ sucursalCodigo: row.sucursal_origen_codigo, lineaNumero: row.linea_numero || row.linea });
  const details = [`${formatNumber(resolveBultos(row), 0)} bultos`, `${formatNumber(resolveKilos(row), 2)} kg`, linea ? `Línea ${linea}` : null].filter(Boolean);
  return <div className="px-3 py-2 text-[11px] text-[rgba(var(--fg),0.76)]">{details.join(' · ')}</div>;
}

function SortHeader({ column, activeSort, onSort, className = '' }) {
  const direction = activeSort.key === column ? activeSort.direction : null;
  return <th className={`px-2 py-1.5 font-semibold ${className}`}><button type="button" onClick={() => onSort(column)} className="inline-flex items-center gap-1 rounded focus:outline-none focus:ring-2 focus:ring-[rgba(var(--ring),0.65)]">{SORTABLE_COLUMNS[column]}<span aria-hidden="true">{direction === 'ASC' ? '↑' : direction === 'DESC' ? '↓' : '↕'}</span></button></th>;
}

function ActionMenuItem({ children, ...props }) {
  return (
    <button
      type="button"
      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-[12px] text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.12)]"
      {...props}
    >
      {children}
    </button>
  );
}

function RowActions({ row, activeSucursalId, user, onPrint, onAnular, isCancelling }) {
  const navigate = useNavigate();
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0 });
  const canAnular = canAnularGuide(row, activeSucursalId, user);
  const canPrint = hasPermission(user, 'GUIAS_REIMPRIMIR') || hasPermission(user, 'GUIAS_VER');

  const closeMenu = useCallback(() => setIsOpen(false), []);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuWidth = 190;
    const viewportPadding = 8;
    const left = Math.min(
      Math.max(viewportPadding, rect.right - menuWidth),
      window.innerWidth - menuWidth - viewportPadding,
    );

    setMenuStyle({
      top: rect.bottom + 4,
      left,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();

    const handlePointerDown = (event) => {
      if (!triggerRef.current) return;
      if (triggerRef.current.contains(event.target)) return;

      const menu = document.getElementById(`guide-row-actions-${row.id}`);
      if (menu?.contains(event.target)) return;
      closeMenu();
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
        triggerRef.current?.focus({ preventScroll: true });
      }
    };

    const handleViewportChange = () => {
      updateMenuPosition();
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closeMenu, isOpen, row.id, updateMenuPosition]);

  const handleToggle = () => {
    if (isOpen) {
      closeMenu();
      return;
    }

    updateMenuPosition();
    setIsOpen(true);
  };

  const handleNavigate = (to, state) => {
    closeMenu();
    navigate(to, state ? { state } : undefined);
  };

  const handlePrint = () => {
    closeMenu();
    onPrint(row);
  };

  const handleAnular = () => {
    closeMenu();
    onAnular(row);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Acciones"
        onClick={handleToggle}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[rgba(var(--border),0.7)] text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgba(var(--ring),0.65)]"
        title="Acciones"
      >
        <MoreHorizontal size={15} aria-hidden="true" />
      </button>
      {isOpen
        ? createPortal(
            <div
              id={`guide-row-actions-${row.id}`}
              role="menu"
              className="fixed z-[var(--z-dropdown)] min-w-[190px] rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgb(var(--panel))] p-1.5 shadow-[0_12px_30px_rgba(var(--shadow),0.18)]"
              style={menuStyle}
            >
              <Link
                to={`/guias/${row.id}`}
                onClick={closeMenu}
                className="flex w-full rounded-lg px-3 py-2 text-[12px] text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.12)]"
              >
                Ver detalle
              </Link>
              <button
                type="button"
                className="flex w-full rounded-lg px-3 py-2 text-left text-[12px] text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.12)]"
                onClick={() =>
                  handleNavigate(`/guias/${row.id}`, {
                    activeTab: 'historial',
                    guiaNumero: resolveGuideNumber(row),
                  })
                }
              >
                Timeline
              </button>
        {canPrint ? (
          <ActionMenuItem onClick={handlePrint}>
            Imprimir guía
          </ActionMenuItem>
        ) : null}
              <button
                type="button"
                className="flex w-full rounded-lg px-3 py-2 text-left text-[12px] text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.12)]"
                onClick={() => handleNavigate(`/cargas/etiquetas?guiaId=${encodeURIComponent(row.id)}`)}
              >
                Reimprimir etiqueta
              </button>
              {canAnular ? (
                <ActionMenuItem onClick={handleAnular} disabled={isCancelling}>
                  {isCancelling ? 'Anulando...' : 'Anular'}
                </ActionMenuItem>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function QuickSearchField({ value, onChange }) {
  return (
    <div className="xl:min-w-0">
      <label htmlFor="busqueda" className="sr-only">
        Búsqueda rápida
      </label>
      <div className="relative">
        <Search
          size={16}
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[rgba(var(--muted),0.82)]"
        />
        <input
          id="busqueda"
          name="busqueda"
          type="text"
          value={value}
          onChange={onChange}
          placeholder="Guía, remito, cliente, remitente o destinatario"
          className={`${FILTER_INPUT_CLASSNAME} pl-9`}
        />
      </div>
    </div>
  );
}

export default function GuiasListado() {
  const navigate = useNavigate();
  const { user, tenant, sucursal, sucursalActiva } = useAuth();
  const [, cambiarEstadoGuia] = useMutation(CAMBIAR_ESTADO_GUIA);
  const [formState, setFormState] = useState(GUIDE_FILTERS_INITIAL_STATE);
  const [activeFilters, setActiveFilters] = useState({});
  const [page, setPage] = useState(1);
  const limit = 20;
  const [result, setResult] = useState({ data: [], total: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [sort, setSort] = useState({ key: 'fecha', direction: 'DESC' });
  const [expandedGuideId, setExpandedGuideId] = useState(null);
  const [expandedGuideData, setExpandedGuideData] = useState({});
  const [expandedGuideLoading, setExpandedGuideLoading] = useState(null);
  const [quickClient, setQuickClient] = useState(null);
  const [viewport, setViewport] = useState(getViewportSize);
  const [cancellingGuideId, setCancellingGuideId] = useState(null);
  const [selectedGuides, setSelectedGuides] = useState(() => new Map());
  const [isPrintingSelection, setIsPrintingSelection] = useState(false);

  const totalPages = useMemo(() => {
    if (!result.total) return 1;
    return Math.max(1, Math.ceil(result.total / limit));
  }, [result.total, limit]);

  const isShortViewport =
    viewport.width <= COMPACT_TABLE_BREAKPOINT || viewport.height <= SHORT_VIEWPORT_HEIGHT;
  const tableColSpan = 9;
  const isCompactTable = viewport.width <= 1440 || viewport.height <= 900;
  const resultsMaxHeight = isShortViewport ? 'calc(100vh - 176px)' : 'calc(100vh - 222px)';

  const loadGuides = useCallback(
    async ({ filters = activeFilters, pageNumber = page } = {}) => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchGuides({ filters, page: pageNumber, limit, sortBy: sort.key, sortDirection: sort.direction });
        setResult({ data: data.data || [], total: data.total || 0 });
      } catch (err) {
        setError(getErrorMessage(err, 'No se pudo cargar el listado de guias.'));
      } finally {
        setIsLoading(false);
      }
    },
    [activeFilters, page, limit, sort]
  );

  useEffect(() => {
    loadGuides({ filters: activeFilters, pageNumber: page });
  }, [activeFilters, page, loadGuides]);

  useEffect(() => {
    const handleResize = () => setViewport(getViewportSize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const activeFiltersText = useMemo(() => summarizeGuideFilters(activeFilters), [activeFilters]);

  const activeSucursalId = useMemo(
    () => getActiveSucursalId({ sucursal, sucursalActiva, user }),
    [sucursal, sucursalActiva, user]
  );

  const handleChange = event => {
    const { name, value } = event.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = event => {
    event.preventDefault();
    const filters = buildGuideFilters(formState);
    setActiveFilters(filters);
    setPage(1);
  };

  const handleReset = () => {
    setFormState(GUIDE_FILTERS_INITIAL_STATE);
    setActiveFilters({});
    setPage(1);
  };

  const handleSort = key => {
    setSort(prev => ({ key, direction: prev.key === key && prev.direction === 'DESC' ? 'ASC' : 'DESC' }));
    setPage(1);
  };

  const copyGuideNumber = useCallback(async value => {
    try {
      await navigator.clipboard?.writeText(value);
    } catch {
      // Clipboard access may be unavailable in non-secure browser contexts.
    }
  }, []);

  const toggleExpandedGuide = useCallback(async row => {
    if (expandedGuideId === row.id) { setExpandedGuideId(null); return; }
    setExpandedGuideId(row.id);
    if (expandedGuideData[row.id] || expandedGuideLoading === row.id) return;
    setExpandedGuideLoading(row.id);
    try {
      const [detail, context] = await Promise.allSettled([fetchGuideDetail(row.id), fetchGuideOperationalContext(row.id)]);
      setExpandedGuideData(prev => ({ ...prev, [row.id]: {
        detail: detail.status === 'fulfilled' ? detail.value : null,
        context: context.status === 'fulfilled' ? context.value : null,
        error: detail.status === 'rejected' && context.status === 'rejected',
      } }));
    } finally {
      setExpandedGuideLoading(null);
    }
  }, [expandedGuideData, expandedGuideId, expandedGuideLoading]);

  const handlePrint = useCallback(async row => {
    try {
      await printGuiaPdf(Number(row.id));
    } catch (err) {
      await Swal.fire({
        title: 'SINA',
        text: err.message || 'No se pudo imprimir la guía.',
        icon: 'error',
        confirmButtonText: 'Entendido',
      });
    }
  }, []);

  const pageGuideIds = useMemo(() => result.data.map((row) => String(row.id)), [result.data]);
  const allPageGuidesSelected = pageGuideIds.length > 0 && pageGuideIds.every((id) => selectedGuides.has(id));
  const somePageGuidesSelected = pageGuideIds.some((id) => selectedGuides.has(id));

  const toggleGuideSelection = useCallback((row, checked) => {
    setSelectedGuides((current) => {
      const next = new Map(current);
      if (checked) next.set(String(row.id), row);
      else next.delete(String(row.id));
      return next;
    });
  }, []);

  const togglePageSelection = useCallback((checked) => {
    setSelectedGuides((current) => {
      const next = new Map(current);
      result.data.forEach((row) => {
        if (checked) next.set(String(row.id), row);
        else next.delete(String(row.id));
      });
      return next;
    });
  }, [result.data]);

  const handlePrintSelection = useCallback(async () => {
    const queue = sortGuidesByNumber(Array.from(selectedGuides.values()));
    if (!queue.length || isPrintingSelection) return;

    setIsPrintingSelection(true);
    const printedIds = [];
    try {
      for (const row of queue) {
        const resultPrint = await printGuiaDirectly(Number(row.id), { user, tenant });
        if (resultPrint.status !== 'printed') {
          const decision = await Swal.fire({
            title: 'Configurá la impresión directa',
            text: 'No se pudo continuar la cola. Revisá Sinamon Print Agent y elegí una impresora de guías disponible.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Abrir configuración',
            cancelButtonText: 'Cerrar',
          });
          if (decision.isConfirmed) {
            navigate('/configuracion/impresion', { state: { returnTo: '/guias' } });
          }
          return;
        }
        printedIds.push(String(row.id));
      }

      await Swal.fire({
        title: 'Impresión enviada',
        text: `${printedIds.length} guía(s) se enviaron en orden numérico.`,
        icon: 'success',
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (printError) {
      await Swal.fire({
        title: 'La cola de impresión se detuvo',
        text: `${printedIds.length} guía(s) fueron enviadas. ${printError?.message || 'Revisá Sinamon Print Agent y volvé a intentar.'}`,
        icon: 'warning',
      });
    } finally {
      if (printedIds.length) {
        setSelectedGuides((current) => {
          const next = new Map(current);
          printedIds.forEach((id) => next.delete(id));
          return next;
        });
      }
      setIsPrintingSelection(false);
    }
  }, [isPrintingSelection, navigate, selectedGuides, tenant, user]);

  const handleAnular = useCallback(
    async row => {
      const decision = await Swal.fire({
        title: `Anular guía ${resolveGuideNumber(row)}`,
        input: 'textarea',
        inputLabel: 'Motivo obligatorio',
        inputPlaceholder: 'Describí la causa de la anulación...',
        inputValidator: value => (!value ? 'La anulación requiere un motivo auditado.' : undefined),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Anular guía',
        cancelButtonText: 'Cancelar',
      });

      if (!decision.isConfirmed) return;

      setCancellingGuideId(row.id);
      try {
        const payload = {
          guia_id: Number(row.id),
          sucursal_id: Number(activeSucursalId || row.sucursal_actual_id || row.sucursal_origen_id || 0),
          to_estado: 'ANULADA',
          descripcion: `Anulación desde listado de guías. Motivo: ${decision.value}`,
          metadata: {
            source: 'guias_listado',
            action: 'anular',
            reason: decision.value,
          },
        };

        const resultMutation = await cambiarEstadoGuia({ input: payload });
        if (resultMutation.error) throw resultMutation.error;

        await loadGuides();
        await Swal.fire('Guía anulada', `La guía ${resolveGuideNumber(row)} fue anulada.`, 'success');
      } catch (err) {
        await Swal.fire('Error', err.message || 'No se pudo anular la guía.', 'error');
      } finally {
        setCancellingGuideId(null);
      }
    },
    [activeSucursalId, cambiarEstadoGuia, loadGuides]
  );

  useOperatorCompactQaScreen('Consulta de guías', [
    'Modo compacto operativo',
    showAdvancedFilters ? 'Filtros avanzados expandidos' : 'Filtros avanzados ocultos',
  ]);

  return (
    <div
      className="mx-auto flex max-w-[1460px] flex-col gap-1.5 px-4 pb-2.5 pt-1 md:px-5 md:pt-1.5"
      data-qa-screen="guias"
    >
      <section
        className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgb(var(--panel))] px-3 py-1.5 shadow-[0_8px_28px_rgba(var(--shadow),0.1)] md:px-4 md:py-2"
        data-qa-filters="true"
      >
        <form className="space-y-1.5" onSubmit={handleSubmit}>
          <h1 className="text-base font-semibold text-[rgb(var(--fg))]">Consulta de guías</h1>

          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-[124px_124px_minmax(0,1fr)_auto]">
            <div>
              <label htmlFor="fromDate" className="sr-only">
                Fecha desde
              </label>
              <input
                id="fromDate"
                name="fromDate"
                type="date"
                value={formState.fromDate}
                onChange={handleChange}
                className={FILTER_INPUT_CLASSNAME}
              />
            </div>

            <div>
              <label htmlFor="toDate" className="sr-only">
                Fecha hasta
              </label>
              <input
                id="toDate"
                name="toDate"
                type="date"
                value={formState.toDate}
                onChange={handleChange}
                className={FILTER_INPUT_CLASSNAME}
              />
            </div>

            <QuickSearchField value={formState.busqueda ?? ''} onChange={handleChange} />

            <button
              type="submit"
              className={`${ACTION_BUTTON_CLASSNAME} border-[rgba(var(--brand),0.95)] bg-[rgb(var(--brand))] px-4 text-[rgb(var(--bg))] shadow-[0_10px_22px_rgba(var(--shadow),0.16)] hover:opacity-90`}
            >
              Buscar
              </button>
            </div>

          <div className="flex flex-wrap items-center justify-between gap-1.5">
            <div className="inline-flex min-h-7 max-w-full items-center gap-1 rounded-full border border-[rgba(var(--border),0.5)] bg-[rgba(var(--subpanel),0.62)] px-2 py-0.5 text-[9px] text-[rgba(var(--muted),0.88)]">
              <span className="font-semibold text-[rgb(var(--fg))]">Activos</span>
              <span className="truncate">{activeFiltersText}</span>
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                className={`${ACTION_BUTTON_CLASSNAME} h-7 border-[rgba(var(--border),0.65)] bg-[rgba(var(--subpanel),0.55)] px-2.5 text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.12)]`}
                onClick={() => setShowAdvancedFilters(prev => !prev)}
                aria-expanded={showAdvancedFilters}
                aria-controls="guias-mas-filtros"
              >
                <span>Más filtros</span>
                <ChevronDown
                  size={14}
                  aria-hidden="true"
                  className={`transition ${showAdvancedFilters ? 'rotate-180' : ''}`}
                />
              </button>
              <button
                type="button"
                className={`${ACTION_BUTTON_CLASSNAME} h-7 border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.8)] px-2.5 text-[rgba(var(--fg),0.88)] hover:bg-[rgba(var(--muted),0.12)]`}
                onClick={handleReset}
              >
                Limpiar
              </button>
            </div>
          </div>

          {showAdvancedFilters ? (
            <div
              id="guias-mas-filtros"
              className="rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--subpanel),0.42)] p-2"
            >
              <GuidesFiltersFields
                formState={formState}
                onChange={handleChange}
                fieldKeys={ADVANCED_FILTER_FIELDS}
              />
            </div>
          ) : null}
        </form>
      </section>

      <section className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgb(var(--panel))] shadow-[0_8px_28px_rgba(var(--shadow),0.1)]">
        <div
          className="flex flex-col gap-0.5 border-b border-[rgba(var(--border),0.35)] px-3 py-1 md:flex-row md:items-center md:justify-between md:px-4"
          data-qa-results-header="true"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
            <h3 className="text-sm font-semibold text-[rgb(var(--fg))]">Resultados</h3>
            <span className="text-[11px] text-[rgba(var(--muted),0.82)]">
              {isLoading ? 'Buscando guías...' : `${result.total} guía(s) encontradas`}
            </span>
            <span className="hidden text-[11px] text-[rgba(var(--muted),0.72)] md:inline">·</span>
            <span className="text-[11px] text-[rgba(var(--muted),0.88)]">
              Página {page} de {totalPages}
            </span>
          </div>
          <button
            type="button"
            className="rounded-xl border border-[rgba(var(--brand),0.55)] bg-[rgba(var(--brand),0.1)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--brand-700))] disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!selectedGuides.size || isPrintingSelection}
            onClick={handlePrintSelection}
          >
            {isPrintingSelection ? 'Imprimiendo en orden…' : `Imprimir seleccionadas (${selectedGuides.size})`}
          </button>
        </div>

        {error ? (
          <div className="px-3 py-4 md:px-4">
            <div className="rounded-2xl border border-[rgba(var(--danger),0.28)] bg-[rgba(var(--danger),0.1)] px-3 py-2 text-sm text-[rgb(var(--danger))]">
              {error}
            </div>
          </div>
        ) : (
          <div style={{ maxHeight: resultsMaxHeight }} className="overflow-x-auto overflow-y-auto" data-qa-results-scroll="true">
            <table className={`w-full min-w-[1120px] table-fixed text-left ${isCompactTable ? 'text-[11px]' : 'text-[12px]'}`}>
              <colgroup>
                <col style={{ width: 34 }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: 76 }} />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-[rgb(var(--panel))] shadow-[inset_0_-1px_0_rgba(var(--border),0.5)]">
  <tr className="text-[9px] uppercase tracking-[0.1em] text-[rgba(var(--muted),0.82)]">
    <th className="px-2 py-1.5 text-center">
      <input
        type="checkbox"
        aria-label="Seleccionar guías de esta página"
        checked={allPageGuidesSelected}
        ref={(input) => { if (input) input.indeterminate = !allPageGuidesSelected && somePageGuidesSelected; }}
        onChange={(event) => togglePageSelection(event.target.checked)}
        disabled={isPrintingSelection || !pageGuideIds.length}
      />
    </th>
    <SortHeader column="guia" activeSort={sort} onSort={handleSort} />
    <SortHeader column="fecha" activeSort={sort} onSort={handleSort} />
    <th className="px-2 py-1.5 font-semibold">Remitente</th><th className="px-2 py-1.5 font-semibold">Destinatario</th>
    <SortHeader column="destino" activeSort={sort} onSort={handleSort} />
    <SortHeader column="estado" activeSort={sort} onSort={handleSort} />
    <SortHeader column="importe" activeSort={sort} onSort={handleSort} className="text-right" />
    <th className="whitespace-nowrap px-2 py-1.5 text-right font-semibold">Acciones</th>
  </tr>
</thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      className="px-4 py-5 text-center text-sm text-[rgba(var(--muted),0.8)]"
                      colSpan={tableColSpan}
                    >
                      Cargando listado...
                    </td>
                  </tr>
                ) : result.data.length ? (
                  result.data.map(row => (
  <Fragment key={row.id}>
    <tr tabIndex={0} role="link" aria-label={`Abrir guía ${resolveFormattedGuideNumber(row)}`} onClick={() => navigate(`/guias/${row.id}`)} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); navigate(`/guias/${row.id}`); } }} className={`cursor-pointer border-t border-[rgba(var(--border),0.35)] align-top transition hover:bg-[rgba(var(--subpanel),0.34)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[rgb(var(--ring))] ${expandedGuideId === row.id ? 'bg-[rgba(var(--brand),.06)]' : ''}`} data-qa-row="true">
      <td className="px-2 py-1 text-center" onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
        <input
          type="checkbox"
          aria-label={`Seleccionar guía ${resolveFormattedGuideNumber(row)}`}
          checked={selectedGuides.has(String(row.id))}
          disabled={isPrintingSelection}
          onChange={(event) => toggleGuideSelection(row, event.target.checked)}
        />
      </td>
      <td className="px-2 py-1"><div className="flex items-start gap-1"><button type="button" aria-label="Mostrar resumen operativo" aria-expanded={expandedGuideId === row.id} onClick={event => { event.stopPropagation(); toggleExpandedGuide(row); }} className="mt-0.5 rounded focus:outline-none focus:ring-2 focus:ring-[rgba(var(--ring),0.65)]"><ChevronRight size={14} className={expandedGuideId === row.id ? 'rotate-90 transition-transform' : 'transition-transform'} /></button><OperationalGuideCell row={row} onCopy={copyGuideNumber} /></div></td>
      <td className="px-2 py-1"><OperationalDateCell row={row} /></td>
      <td className="px-2 py-1"><OperationalParticipantCell row={row} participantKey="remitente" label="Remitente" onOpenClient={setQuickClient} /></td><td className="px-2 py-1"><OperationalParticipantCell row={row} participantKey="destinatario" label="Destinatario" onOpenClient={setQuickClient} /></td>
      <td className="px-2 py-1"><DestinationCell row={row} /></td><td className="px-2 py-1"><OperationalStatusCell row={row} /></td><td className="px-2 py-1"><OperationalAmountCell row={row} /></td>
      <td className="px-2 py-1 text-right" onClick={event => event.stopPropagation()}><RowActions row={row} activeSucursalId={activeSucursalId} user={user} onPrint={handlePrint} onAnular={handleAnular} isCancelling={cancellingGuideId === row.id} /></td>
    </tr>
    {expandedGuideId === row.id ? <tr className="border-t border-[rgba(var(--border),0.25)] bg-[rgba(var(--subpanel),0.28)]"><td colSpan={tableColSpan}><ExpandedGuideRow row={row} detail={expandedGuideData[row.id]?.detail} loading={expandedGuideLoading === row.id} /><div className="flex gap-2 px-3 pb-2 text-xs"><button type="button" onClick={() => navigate(`/guias/${row.id}`)} className="font-semibold text-[rgb(var(--brand))]">Ver guía completa</button><button type="button" onClick={() => handlePrint(row)} className="font-semibold text-[rgb(var(--brand))]">Imprimir</button></div></td></tr> : null}
  </Fragment>
))) : (
                  <tr>
                    <td
                      className="px-4 py-5 text-center text-sm text-[rgba(var(--muted),0.8)]"
                      colSpan={tableColSpan}
                    >
                      No hay guías para los filtros seleccionados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[rgba(var(--border),0.4)] px-3 py-1.5 text-sm md:px-4 md:py-2">
          <span className="text-[rgba(var(--muted),0.85)]">
            Mostrando {result.data.length} de {result.total || 0}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-xl border border-[rgba(var(--border),0.7)] px-3 py-1 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.12)] disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={page <= 1 || isLoading}
            >
              Anterior
            </button>
            <button
              type="button"
              className="rounded-xl border border-[rgba(var(--border),0.7)] px-3 py-1 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.12)] disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages || isLoading}
            >
              Siguiente
            </button>
          </div>
        </div>
      </section>
      {quickClient ? <ClienteQuickViewModal clientId={quickClient.id} snapshot={quickClient} user={user} onClose={() => setQuickClient(null)} /> : null}
    </div>
  );
}
